import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import { getLoanSummaryRequest } from 'tio-data/requests/user';

const EMPTY_LOAN_SUMMARY: LoanSummary = { summaryByLoanType: [], errors: [] };

export default class AuthenticatedRepayingStudentDebtRepaymentStrategyFinderDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id as string;

    // Use allSettled so individual API failures don't crash the entire page
    const [loanSummaryResult, recsResult, personResult] = await Promise.allSettled([
      userId
        ? this.store.request(getLoanSummaryRequest(userId))
        : Promise.resolve({ content: EMPTY_LOAN_SUMMARY }),
      // WarpDrive builder read (Phase 2.4) — replaces store.query;
      // flattened bracket keys satisfy the builder's QueryParamsSource type.
      this.store.request(
        query<RecommendationModel>('recommendation', {
          'filter[user]': userId as string,
          'filter[requesting-app]': 'STRATEGY_FINDER',
          include: 'user',
          sort: '-created-at',
          'page[offset]': 0,
          'page[limit]': 3,
        })
      ) as Promise<{ content: { data: RecommendationModel[] } }>,
      // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
      // reload: true maps to the builder's reload option (3rd arg).
      this.store.request(
        findRecord<PersonModel>('person', personId, {
          include: 'accounts.financial-institution,accounts.loans,user.answer-profile',
          reload: true,
        })
      ) as Promise<{ content: { data: PersonModel } }>,
    ]);

    const loanSummary =
      loanSummaryResult.status === 'fulfilled'
        ? ((loanSummaryResult.value.content as LoanSummary | undefined) ?? EMPTY_LOAN_SUMMARY)
        : EMPTY_LOAN_SUMMARY;
    const allRecommendations =
      recsResult.status === 'fulfilled' ? recsResult.value.content.data : [];
    const person = personResult.status === 'fulfilled' ? personResult.value.content.data : null;

    return {
      accounts: person?.accounts ?? [],
      allRecommendations,
      loanSummary,
      mostRecentRecommendation: allRecommendations?.[0],
      person,
      user: person?.user ?? null,
    };
  }
}
